import React, {useEffect, useState, useRef, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    getUser,
    login,
    signUp,
    LOGIN_FAILURE,
    SIGN_UP_FAILURE,
} from '@core/actions/UserActions';
import {getAuthOptions} from '@core/actions/AuthOptionActions';
import {TimeOffset} from '@form/helpers/TimeHelper';
import {optionItemFinder} from '@form/helpers/OptionHelper';
import {
    HTTP_STATUS_ACCESS_DENIED,
    HTTP_STATUS_CODE_EMAIL_SUGGESTION,
    HTTP_STATUS_CODE_FORBIDDEN_EMAIL_DOMAIN,
    HTTP_STATUS_CODE_INVALID_PASSWORD,
    HTTP_STATUS_CODE_USER_DOES_NOT_EXIST,
    HTTP_STATUS_CODE_USER_EXISTS,
} from '@common/constants/HttpStatusCode';
import useInvisibleRecaptcha from '@core/hooks/useInvisibleRecaptcha';
import {
    CAPTCHA_CONFIRM_MESSAGE,
    CAPTCHA_ERRORS,
    CAPTCHA_FAILED_MESSAGE,
    CAPTCHA_MAX_ATTEMPTS,
} from '@core/constants/CaptchaConstants';
import {resolveReturnUrl} from '@common/helpers/loginRedirect';
import ResumeLoginForm from './ResumeLoginForm';
import ResumeRegisterForm from './ResumeRegisterForm';
import ResumeResetPassword from './ResumeResetPassword';

function Index() {
    const dispatch = useDispatch();
    const {authorized, registered, ipIsoCode} = useSelector(state => state.user);
    const {countries} = useSelector(state => state.authOptions.data);
    const {signUpEnabled} = useSelector(state => state.site);
    const {execute: executeRecaptcha, enabled: recaptchaEnabled} = useInvisibleRecaptcha();
    const [submitting, setSubmitting] = useState(false);
    const returnUrlRef = useRef(resolveReturnUrl());

    const resolveView = () => {
        const query = new URLSearchParams(window.location.search).get('view');
        if (query === 'register' || query === 'reset-password' || query === 'login') return query;
        if (window.location.pathname.includes('/reset-password')) return 'reset-password';
        return 'login';
    };

    const buildUrlForView = (view) => {
        const url = new URL(window.location.href);
        if (view === 'login') {
            url.searchParams.delete('view');
        } else {
            url.searchParams.set('view', view);
        }
        return url.pathname + url.search + url.hash;
    };

    const [activeView, setActiveView] = useState(resolveView);
    const initialResetEmailRef = useRef(new URLSearchParams(window.location.search).get('email') || '');
    const loginEmailRef = useRef(initialResetEmailRef.current);

    useEffect(() => {
        dispatch(getUser());
        dispatch(getAuthOptions());
    }, []);

    useEffect(() => {
        const onPopState = () => setActiveView(resolveView());
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    const navigateTo = useCallback((view) => {
        window.history.pushState(null, '', buildUrlForView(view));
        const titles = {
            'login': 'Login',
            'register': 'Create Account',
            'reset-password': 'Reset Password',
        };
        document.title = titles[view] || 'Login';
        setActiveView(view);
    }, []);

    const handleForgotPassword = useCallback(() => navigateTo('reset-password'), [navigateTo]);
    const handleSignIn = useCallback(() => navigateTo('login'), [navigateTo]);
    const handleSwitchToRegister = useCallback(() => navigateTo('register'), [navigateTo]);
    const handleSwitchToLogin = useCallback(() => navigateTo('login'), [navigateTo]);

    const getErrorCode = response => {
        const payload = response && response.payload ? response.payload : {};
        const responseBody = payload && payload.response ? payload.response : {};

        if (responseBody && typeof responseBody.message === 'string') {
            return responseBody.message;
        }

        if (responseBody && responseBody.errors && typeof responseBody.errors.detail === 'string') {
            return responseBody.errors.detail;
        }

        return null;
    };

    const handleLoginSubmit = async (values) => {
        if (authorized) return;

        setSubmitting(true);
        const response = await dispatch(login(values));

        if (response.type === LOGIN_FAILURE) {
            setSubmitting(false);

            const errors = {};

            switch (response.payload.status) {
                case HTTP_STATUS_ACCESS_DENIED:
                    errors.email = 'Access is denied!';
                    break;
                case HTTP_STATUS_CODE_USER_DOES_NOT_EXIST:
                    errors.email = 'User doesn\'t exist.';
                    break;
                case HTTP_STATUS_CODE_INVALID_PASSWORD:
                    errors.password = 'Password is incorrect.';
                    break;
                default:
                    errors.email = 'Try again later.';
            }

            return errors;
        }

        return undefined;
    };

    const handleRegisterSubmit = async (values, recaptchaResponse = '', captchaAttempt = 0) => {
        if (authorized) return;

        const country = values.phone && optionItemFinder(values.phone.code, countries);
        if (!country) {
            return {phone: {number: 'Please select a country code'}};
        }

        setSubmitting(true);

        let nextRecaptchaResponse = recaptchaResponse;
        let nextCaptchaAttempt = captchaAttempt;

        if (!nextRecaptchaResponse && recaptchaEnabled && nextCaptchaAttempt === 0) {
            const recaptchaToken = await executeRecaptcha();
            if (recaptchaToken) {
                nextRecaptchaResponse = recaptchaToken;
                nextCaptchaAttempt = 1;
            }
        }

        const data = {
            email: values.email,
            name: 'Customer',
            phone: country.code + ' ' + values.phone.number,
            user_time_offset: TimeOffset(),
            subscribe: values.subscribe ? 1 : 0,
            country_iso: country.iso,
        };

        if (nextRecaptchaResponse) {
            data.recaptchaResponse = nextRecaptchaResponse;
        }

        const response = await dispatch(signUp(data));

        if (response.type === SIGN_UP_FAILURE) {
            const errorCode = getErrorCode(response);

            if (CAPTCHA_ERRORS.includes(errorCode)) {
                if (!recaptchaEnabled || nextCaptchaAttempt >= CAPTCHA_MAX_ATTEMPTS) {
                    setSubmitting(false);
                    return {email: CAPTCHA_CONFIRM_MESSAGE};
                }

                const recaptchaToken = await executeRecaptcha();
                if (!recaptchaToken) {
                    setSubmitting(false);
                    return {email: CAPTCHA_FAILED_MESSAGE};
                }

                return handleRegisterSubmit(values, recaptchaToken, nextCaptchaAttempt + 1);
            }

            setSubmitting(false);
            const errors = {};

            switch (response.payload.status) {
                case HTTP_STATUS_ACCESS_DENIED:
                    errors.email = 'Access is denied!';
                    break;
                case HTTP_STATUS_CODE_USER_EXISTS:
                    errors.email = 'User already exists.';
                    break;
                case HTTP_STATUS_CODE_FORBIDDEN_EMAIL_DOMAIN:
                    errors.email = 'Forbidden email domain.';
                    break;
                case HTTP_STATUS_CODE_EMAIL_SUGGESTION:
                    errors.email = `Did you mean ${response.payload.response.message} ?`;
                    break;
                default:
                    errors.email = 'Try again later.';
            }

            return errors;
        }

        return undefined;
    };

    useEffect(() => {
        if (authorized !== true) return;

        const timer = setTimeout(() => {
            window.location.href = returnUrlRef.current || (registered ? '/account/order/' : '/account/');
        }, 800);

        return () => clearTimeout(timer);
    }, [authorized, registered]);

    const isLoadingData = authorized === undefined
        || !countries
        || (activeView === 'register' && !ipIsoCode);

    if (isLoadingData) {
        return (
            <div id="resume-auth">
                <div className="resume-preloader__overlay">
                    <div className="resume-preloader__spinner" />
                </div>
            </div>
        );
    }

    const renderAuthTabs = () => {
        const isLogin = activeView === 'login';
        const isRegister = activeView === 'register';

        return (
            <div className="resume-auth__card">
                {isLogin && (
                    <div className="resume-auth__header">
                        <div className="resume-auth__header-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                                <polyline points="10 17 15 12 10 7" />
                                <line x1="15" y1="12" x2="3" y2="12" />
                            </svg>
                        </div>
                        <h2 className="resume-auth__title">Welcome back</h2>
                        <p className="resume-auth__subtitle">Sign in to manage your orders and profile</p>
                        {signUpEnabled && (
                            <p className="resume-auth__switch-text">
                                Don't have an account yet?
                                <a
                                    onClick={handleSwitchToRegister}
                                    className="resume-auth__switch-link"
                                    role="button"
                                    tabIndex={0}
                                >
                                    Create your account
                                </a>
                            </p>
                        )}
                    </div>
                )}

                {isRegister && (
                    <div className="resume-auth__header">
                        <div className="resume-auth__header-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="8.5" cy="7" r="4" />
                                <line x1="20" y1="8" x2="20" y2="14" />
                                <line x1="23" y1="11" x2="17" y2="11" />
                            </svg>
                        </div>
                        <h2 className="resume-auth__title">Create your account</h2>
                        <p className="resume-auth__subtitle">Get started with your first order in seconds</p>
                        <p className="resume-auth__switch-text">
                            Already have an account?
                            <a
                                onClick={handleSwitchToLogin}
                                className="resume-auth__switch-link"
                                role="button"
                                tabIndex={0}
                            >
                                Log in
                            </a>
                        </p>
                    </div>
                )}

                {isLogin && (
                    <ResumeLoginForm
                        onSubmit={handleLoginSubmit}
                        onForgotPassword={handleForgotPassword}
                        emailRef={loginEmailRef}
                    />
                )}

                {isRegister && (
                    <ResumeRegisterForm
                        onSubmit={handleRegisterSubmit}
                    />
                )}
            </div>
        );
    };

    return (
        <div id="resume-auth">
            <div className="resume-auth__container">
                {activeView === 'reset-password' ? (
                    <div className="resume-auth__card">
                        <ResumeResetPassword
                            initialEmail={loginEmailRef.current || initialResetEmailRef.current}
                            onSignIn={handleSignIn}
                        />
                    </div>
                ) : (
                    renderAuthTabs()
                )}
            </div>
            {(submitting || (authorized === true && activeView !== 'reset-password')) && (
                <div className="resume-preloader__overlay">
                    <div className="resume-preloader__spinner" />
                </div>
            )}
        </div>
    );
}

export default Index;
